<template>
    <UModal :model-value="open" @update:model-value="$emit('update:open', $event)" :ui="{ width: 'sm:max-w-3xl' }">
        <UCard :ui="{ rounded: 'rounded-lg', header: { background: 'bg-gray-50' } }">
            <template #header>
                <div class="flex items-center justify-between">
                    <div>
                        <h3 class="text-base font-medium leading-6 text-gray-800">Non-member switches</h3>
                        <p class="text-xs text-gray-500 mt-1">
                            App calls this customer placed that an operator switched to someone with no active membership.
                        </p>
                    </div>
                    <UButton color="gray" variant="ghost" icon="i-ph-x-bold" class="-my-1" @click="$emit('update:open', false)" />
                </div>
            </template>

            <UTable
                :rows="incidents"
                :columns="columns"
                :loading="loading"
                :loading-state="{ icon: 'i-ph-circle-notch-bold', label: '' }"
                :empty-state="{ icon: 'i-ph-user-switch-duotone', label: 'No switched calls.' }"
            >
                <template #call-data="{ row }">
                    <ULink :to="`/calls/${row.call_id}`" class="font-mono hover:underline">
                        {{ row.call_number }}
                    </ULink>
                </template>
                <template #beneficiary-data="{ row }">
                    <div class="leading-5">
                        <p>{{ row.beneficiaryName || '–' }}</p>
                        <span v-if="row.beneficiaryUpdatedAt" class="text-xs text-gray-500">
                            Record last touched {{ dayjs(row.beneficiaryUpdatedAt).format('lll') }}
                        </span>
                    </div>
                </template>
                <template #switched_at-data="{ row }">
                    <span>{{ dayjs(row.switched_at).format('lll') }}</span>
                </template>
                <template #operator-data="{ row }">
                    <span>{{ row.operatorName || '–' }}</span>
                </template>
            </UTable>
        </UCard>
    </UModal>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import LocalizedFormat from 'dayjs/plugin/localizedFormat'

dayjs.extend(LocalizedFormat)

type Incident = {
    call_id: string
    call_number: number
    switched_at: string
    beneficiary_customer_id: string
    operator_user_id: string | null
    beneficiaryName?: string
    beneficiaryUpdatedAt?: string
    operatorName?: string
}

defineEmits<{ 'update:open': [boolean] }>()
const props = defineProps<{ open: boolean, customerId?: string }>()

const client = useSupabaseClient()

const loading = ref(false)
const incidents = ref<Incident[]>([])

const columns = [
    { key: 'call', label: 'Call' },
    { key: 'beneficiary', label: 'Switched To' },
    { key: 'switched_at', label: 'Switched' },
    { key: 'operator', label: 'Operator' },
]

watch(() => props.open, (open) => {
    if (open) {
        fetchData()
    }
})

async function fetchData() {
    if (!props.customerId) {
        return
    }

    loading.value = true
    incidents.value = []

    const { data, error } = await client
        .from('membership_switch_incidents')
        .select('call_id, call_number, switched_at, beneficiary_customer_id, operator_user_id')
        .eq('caller_customer_id', props.customerId)
        .order('switched_at', { ascending: false })

    if (error || !data) {
        console.error('Failed to load switched calls', error)
        loading.value = false
        return
    }

    const rows = data as unknown as Incident[]
    const beneficiaryIds = Array.from(new Set(rows.map(r => r.beneficiary_customer_id).filter(Boolean)))
    const operatorIds = Array.from(new Set(rows.map(r => r.operator_user_id).filter(Boolean))) as string[]

    const [beneficiaries, operators] = await Promise.all([
        beneficiaryIds.length
            ? client.from('customers').select('id, first_name, middle_name, last_name, updated_at').in('id', beneficiaryIds)
            : Promise.resolve({ data: [] }),
        operatorIds.length
            ? client.from('users').select('id, name').in('id', operatorIds)
            : Promise.resolve({ data: [] }),
    ])

    const nameById = new Map(
        ((beneficiaries.data ?? []) as any[]).map(c => [
            c.id,
            { name: [c.first_name, c.middle_name, c.last_name].filter(Boolean).join(' '), updatedAt: c.updated_at },
        ]),
    )
    const operatorById = new Map(((operators.data ?? []) as any[]).map(u => [u.id, u.name]))

    incidents.value = rows.map(row => ({
        ...row,
        beneficiaryName: nameById.get(row.beneficiary_customer_id)?.name,
        beneficiaryUpdatedAt: nameById.get(row.beneficiary_customer_id)?.updatedAt,
        operatorName: row.operator_user_id ? operatorById.get(row.operator_user_id) : undefined,
    }))

    loading.value = false
}
</script>
